/**
 * DFP Insights Page
 * =================
 * Dedicated, route-based DFP analysis hub. Consolidates the previously
 * scattered DFP surfaces into one place:
 *
 *   - Alerts    : high-precision actionable feed (watchlist / cotraveler /
 *                 proximity / personal hotspot), reusing DFPAlertsTab with
 *                 category=actionable.
 *   - Insights  : the noisier algorithmic anomaly feed (evil twin, rogue AP,
 *                 BLE flood, GNSS, etc.; category=insights).
 *   - Watchlist : GLOBAL watchlist manager (closes the per-geofence gap).
 *   - Trusted   : trusted-device (exclusion) manager.
 *   - Baseline  : run a baseline scan over a geofence/time window.
 *   - Detectors : detector health + feedback/calibration (FP rates).
 *   - Proximity : operator -> ATAK callsign mapping for directed "near you"
 *                 warnings, plus review of proximity/cotraveler alerts.
 *
 * The COP ForceProtectionDialog and Settings SystemMetrics surfaces remain
 * unchanged; this page is additive.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Tabs, Tab, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, TextField, MenuItem, Alert, CircularProgress, IconButton,
  Button, FormControl, InputLabel, Select, Checkbox, Tooltip, Divider,
} from '@mui/material';
import {
  Delete as DeleteIcon, Refresh as RefreshIcon, Warning as WarningIcon,
  VerifiedUser as TrustIcon, Shield as ShieldIcon, Insights as InsightsIcon,
  PlaylistAddCheck as WatchlistIcon, MonitorHeart as HealthIcon,
  MyLocation as ProximityIcon, FactCheck as BaselineIcon,
} from '@mui/icons-material';

import DFPAlertsTab from '../components/cop/DFPAlertsTab';
import { DFPTrustedWifiProfilesPanel } from '../components/admin/SystemMetrics';
import {
  dfpAdminService, WatchlistEntry, TrustedDevice, BaselineDevice,
  OperatorTakMapping,
} from '../services/dfpAdminService';
import { dfpAlertsService } from '../services/dfpAlertsService';

const SIGNAL_TYPES = ['wifi', 'bluetooth', 'cellular'];

const fmtAge = (ts?: string | null) => {
  if (!ts) return '—';
  const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (mins < 1) return '<1m';
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h`;
  return `${Math.floor(mins / 1440)}d`;
};

// ---------------------------------------------------------------------------
// Watchlist (global) panel
// ---------------------------------------------------------------------------

const watchPlace = (row: WatchlistEntry): string => {
  if (row.latitude == null || row.longitude == null) return '—';
  return `${row.latitude.toFixed(5)}, ${row.longitude.toFixed(5)}`;
};

const watchWindow = (row: WatchlistEntry): string => {
  const fmt = (value?: string | null) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };
  const from = fmt(row.first_seen);
  const to = fmt(row.last_seen);
  if (from && to && from !== to) return `${from} – ${to}`;
  return to || from || '—';
};

const WatchlistPanel: React.FC = () => {
  const [rows, setRows] = useState<WatchlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [mode, setMode] = useState<'exact' | 'pattern'>('exact');
  const [identifier, setIdentifier] = useState('');
  const [pattern, setPattern] = useState('');
  const [type, setType] = useState('mac');
  const [signal, setSignal] = useState('any');
  const [severity, setSeverity] = useState('critical');
  const [label, setLabel] = useState('');
  const [geofenceId, setGeofenceId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await dfpAdminService.listWatchlist());
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load watchlist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setError(null);
    try {
      await dfpAdminService.createWatchlist({
        identifier: mode === 'exact' ? identifier : undefined,
        identifier_type: type,
        match_mode: mode,
        pattern: mode === 'pattern' ? pattern : undefined,
        signal_type: signal,
        severity_floor: severity,
        geofence_id: geofenceId.trim() ? Number(geofenceId) : undefined,
        label: label || undefined,
      });
      setIdentifier(''); setPattern(''); setLabel('');
      setInfo('Watchlist entry added.');
      load();
    } catch (e: any) {
      setError(e.message || 'Could not add entry');
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Global threat watchlist. A match forces a high-severity alert even when no detector
        would otherwise fire. Leave Geofence ID blank for a global entry (applies everywhere).
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <FormControl size="small" sx={{ width: 120 }}>
          <InputLabel>Match</InputLabel>
          <Select value={mode} label="Match" onChange={e => setMode(e.target.value as any)}>
            <MenuItem value="exact">Exact</MenuItem>
            <MenuItem value="pattern">Pattern</MenuItem>
          </Select>
        </FormControl>
        {mode === 'exact' ? (
          <TextField size="small" label="Identifier (MAC/BSSID/SSID)" value={identifier}
            onChange={e => setIdentifier(e.target.value)} sx={{ width: 240 }} />
        ) : (
          <TextField size="small" label="Pattern (OUI hex or regex)" value={pattern}
            onChange={e => setPattern(e.target.value)} sx={{ width: 240 }} />
        )}
        <FormControl size="small" sx={{ width: 120 }}>
          <InputLabel>Type</InputLabel>
          <Select value={type} label="Type" onChange={e => setType(e.target.value)}>
            {['mac', 'bssid', 'ssid', 'name', 'oui', 'device_id', 'other'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 120 }}>
          <InputLabel>Signal</InputLabel>
          <Select value={signal} label="Signal" onChange={e => setSignal(e.target.value)}>
            {['any', 'wifi', 'bluetooth', 'cellular'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 120 }}>
          <InputLabel>Severity</InputLabel>
          <Select value={severity} label="Severity" onChange={e => setSeverity(e.target.value)}>
            {['info', 'warning', 'critical'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label="Label" value={label} onChange={e => setLabel(e.target.value)} sx={{ width: 150 }} />
        <TextField size="small" label="Geofence ID (optional)" value={geofenceId}
          onChange={e => setGeofenceId(e.target.value.replace(/[^0-9]/g, ''))} sx={{ width: 160 }} />
        <Button variant="contained" onClick={add}
          disabled={mode === 'exact' ? !identifier.trim() : !pattern.trim()}>Add</Button>
        <IconButton onClick={load}><RefreshIcon /></IconButton>
      </Box>

      {loading ? <CircularProgress size={24} /> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Match</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Signal</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Last place</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(w => (
              <TableRow key={w.id} hover>
                <TableCell>{w.match_mode}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{w.normalized_identifier || w.pattern}</TableCell>
                <TableCell>{w.signal_type}</TableCell>
                <TableCell><Chip size="small" color={w.severity_floor === 'critical' ? 'error' : w.severity_floor === 'warning' ? 'warning' : 'default'} label={w.severity_floor} /></TableCell>
                <TableCell>{w.geofence_id == null ? 'global' : `fence ${w.geofence_id}`}</TableCell>
                <TableCell>{w.label || '—'}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{watchPlace(w)}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{watchWindow(w)}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={async () => { await dfpAdminService.deleteWatchlist(w.id); load(); }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={9}><Typography variant="body2" color="text.secondary">No watchlist entries.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Trusted devices (exclusions) panel
// ---------------------------------------------------------------------------

const TrustedPanel: React.FC = () => {
  const [rows, setRows] = useState<TrustedDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [identifier, setIdentifier] = useState('');
  const [type, setType] = useState('mac');
  const [appliesTo, setAppliesTo] = useState('all');
  const [label, setLabel] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await dfpAdminService.listTrusted());
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load trusted devices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setError(null);
    try {
      await dfpAdminService.createTrusted({
        identifier: identifier.trim(),
        identifier_type: type,
        applies_to: appliesTo,
        label: label || undefined,
        reason: 'Added from DFP Insights',
      });
      setIdentifier(''); setLabel('');
      load();
    } catch (e: any) {
      setError(e.message || 'Could not add trusted device');
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Trusted (excluded) devices are suppressed from the noisy detectors. Security-critical
        detectors (watchlist, cotraveler, evil-twin, GNSS/IMSI spoofing) still fire regardless.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <TextField size="small" label="Identifier (MAC/IP/ID)" value={identifier}
          onChange={e => setIdentifier(e.target.value)} sx={{ width: 240 }} />
        <FormControl size="small" sx={{ width: 130 }}>
          <InputLabel>Type</InputLabel>
          <Select value={type} label="Type" onChange={e => setType(e.target.value)}>
            {['mac', 'ip', 'device_id', 'ssid', 'name', 'other'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 130 }}>
          <InputLabel>Applies to</InputLabel>
          <Select value={appliesTo} label="Applies to" onChange={e => setAppliesTo(e.target.value)}>
            {['all', 'wifi', 'bluetooth', 'cotraveler'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label="Label" value={label} onChange={e => setLabel(e.target.value)} sx={{ width: 160 }} />
        <Button variant="contained" startIcon={<TrustIcon />} onClick={add} disabled={!identifier.trim()}>Trust</Button>
        <IconButton onClick={load}><RefreshIcon /></IconButton>
      </Box>

      {loading ? <CircularProgress size={24} /> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Identifier</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Applies to</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Reviewed</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(t => (
              <TableRow key={t.id} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{t.identifier}</TableCell>
                <TableCell>{t.label || '—'}</TableCell>
                <TableCell>{t.applies_to}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={(t.source || 'manual').replace('auto_', 'auto: ')}
                    color={t.source && t.source.startsWith('auto_') ? 'info' : 'default'}
                    variant={t.source && t.source.startsWith('auto_') ? 'filled' : 'outlined'}
                    title={t.expires_at ? `Expires ${new Date(t.expires_at).toLocaleString()}` : 'Never expires'}
                  />
                </TableCell>
                <TableCell>{t.geofence_id == null ? 'global' : `fence ${t.geofence_id}`}</TableCell>
                <TableCell>{fmtAge(t.last_reviewed_at)}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={async () => { await dfpAdminService.deleteTrusted(t.id); load(); }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No trusted devices.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {/* Trusted-WiFi-profile management, relocated here from Settings so
          all trust surfaces live together. */}
      <Divider sx={{ my: 3 }} />
      <DFPTrustedWifiProfilesPanel />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Baseline panel
// ---------------------------------------------------------------------------

const BaselinePanel: React.FC = () => {
  const [geofenceId, setGeofenceId] = useState('');
  const [lookbackHours, setLookbackHours] = useState(24);
  const [minSightings, setMinSightings] = useState(2);
  const [signalTypes, setSignalTypes] = useState<string[]>([...SIGNAL_TYPES]);
  const [devices, setDevices] = useState<BaselineDevice[]>([]);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const scan = async () => {
    if (!geofenceId.trim()) { setError('Enter a geofence ID to scan.'); return; }
    setScanning(true); setError(null);
    try {
      const end = new Date().toISOString();
      const start = new Date(Date.now() - lookbackHours * 3600_000).toISOString();
      const resp = await dfpAdminService.baselineScan({
        geofence_id: Number(geofenceId),
        start, end,
        signal_types: signalTypes,
        min_sightings: minSightings,
      });
      setDevices(resp.devices);
      setInfo(resp.devices.length === 0 ? 'No devices found in this window.' : `Found ${resp.devices.length} device(s).`);
    } catch (e: any) {
      setError(e.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const trust = async (d: BaselineDevice) => {
    try {
      await dfpAdminService.createTrusted({
        identifier: d.identifier || d.normalized_identifier,
        identifier_type: d.signal_type === 'cellular' ? 'device_id' : 'mac',
        applies_to: d.signal_type === 'wifi' ? 'wifi' : d.signal_type === 'bluetooth' ? 'bluetooth' : 'all',
        label: d.display_name || d.manufacturer || undefined,
        reason: `Baseline-trusted for fence ${geofenceId}`,
        geofence_id: Number(geofenceId),
      });
      setInfo(`Trusted ${d.identifier || d.normalized_identifier}.`);
    } catch (e: any) {
      setError(e.message || 'Trust failed');
    }
  };

  const watchlist = async (d: BaselineDevice) => {
    try {
      await dfpAdminService.createWatchlist({
        identifier: d.identifier || d.normalized_identifier,
        identifier_type: d.signal_type === 'cellular' ? 'device_id' : 'mac',
        match_mode: 'exact',
        signal_type: d.signal_type,
        severity_floor: 'critical',
        label: d.display_name || d.manufacturer || 'Flagged from baseline',
      });
      setInfo(`Added ${d.identifier || d.normalized_identifier} to watchlist.`);
    } catch (e: any) {
      setError(e.message || 'Watchlist add failed');
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Aggregate the unique signal-emitting devices seen inside a geofence over a time window,
        then trust the friendlies or flag threats.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
        <TextField size="small" label="Geofence ID" value={geofenceId}
          onChange={e => setGeofenceId(e.target.value.replace(/[^0-9]/g, ''))} sx={{ width: 130 }} />
        <TextField label="Lookback (hours)" type="number" size="small" value={lookbackHours}
          onChange={e => setLookbackHours(Math.max(1, parseInt(e.target.value) || 1))} sx={{ width: 140 }} />
        <TextField label="Min sightings" type="number" size="small" value={minSightings}
          onChange={e => setMinSightings(Math.max(1, parseInt(e.target.value) || 1))} sx={{ width: 130 }} />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Signal types</InputLabel>
          <Select multiple value={signalTypes} label="Signal types"
            renderValue={(v) => (v as string[]).join(', ')}
            onChange={e => setSignalTypes(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}>
            {SIGNAL_TYPES.map(s => (
              <MenuItem key={s} value={s}><Checkbox checked={signalTypes.indexOf(s) > -1} size="small" />{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={scanning ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={scan} disabled={scanning}>Run Baseline Scan</Button>
      </Box>

      {devices.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Identifier</TableCell>
              <TableCell>Name / Vendor</TableCell>
              <TableCell align="right">Sightings</TableCell>
              <TableCell align="right">Last seen</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.map(d => (
              <TableRow key={`${d.signal_type}:${d.normalized_identifier}`} hover>
                <TableCell>{d.signal_type}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{d.identifier || d.normalized_identifier}</TableCell>
                <TableCell>{d.display_name || d.manufacturer || '—'}</TableCell>
                <TableCell align="right">{d.sighting_count}</TableCell>
                <TableCell align="right">{fmtAge(d.last_seen)}</TableCell>
                <TableCell>
                  {d.already_trusted && <Chip size="small" color="success" label="trusted" />}
                  {d.on_watchlist && <Chip size="small" color="error" label="watchlist" sx={{ ml: 0.5 }} />}
                  {d.is_tracker && <Chip size="small" color="warning" label="tracker" sx={{ ml: 0.5 }} />}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Trust (exclude)">
                    <IconButton size="small" color="success" disabled={d.already_trusted} onClick={() => trust(d)}>
                      <TrustIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Add to watchlist">
                    <IconButton size="small" color="warning" disabled={d.on_watchlist} onClick={() => watchlist(d)}>
                      <WarningIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Detectors & Health panel
// ---------------------------------------------------------------------------

const HealthPanel: React.FC = () => {
  const [health, setHealth] = useState<any>(null);
  const [feedback, setFeedback] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, f] = await Promise.allSettled([
        dfpAlertsService.getHealth(),
        dfpAdminService.getFeedbackSummary(),
      ]);
      if (h.status === 'fulfilled') setHealth(h.value);
      if (f.status === 'fulfilled') setFeedback(f.value);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <CircularProgress size={24} />;

  const detectors: any[] = health?.detectors || [];
  const fbRows: any[] = feedback?.summary || [];
  const pipeline = health?.verdict_pipeline || {};
  const ledger = pipeline.ledger;
  const activity = pipeline.verdict_activity;
  const refdata = pipeline.reference_data;

  // A detector that hasn't heartbeat in >24h is dormant, not "learning".
  const isStale = (ts?: string | null) => !ts || (Date.now() - new Date(ts).getTime()) > 24 * 3600 * 1000;
  const refdataStale = refdata?.opencellid_last_refresh
    ? (Date.now() - new Date(refdata.opencellid_last_refresh).getTime()) > 8 * 24 * 3600 * 1000
    : true;

  const statCard = (title: string, rows: Array<[string, React.ReactNode]>) => (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 220, flex: '1 1 220px' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</Typography>
      <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {rows.map(([k, v], i) => (
          <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">{k}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>{v}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Verdict pipeline health — the evidence-based decision layer */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1">Verdict pipeline</Typography>
        {activity?.last_evaluated_at && (
          <Chip size="small"
            label={`last evaluation ${fmtAge(activity.last_evaluated_at)} ago`}
            color={(Date.now() - new Date(activity.last_evaluated_at).getTime()) < 15 * 60000 ? 'success' : 'warning'} />
        )}
        <IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
        {ledger && statCard('Entity ledger', [
          ['Entities tracked', Number(ledger.total).toLocaleString()],
          ['New / Watch', `${Number(ledger.state_new).toLocaleString()} / ${ledger.state_watch}`],
          ['Established', ledger.state_established],
          ['Confirmed threat', <span style={{ color: Number(ledger.state_confirmed) > 0 ? '#f44336' : undefined }}>{ledger.state_confirmed}</span>],
          ['Inconclusive', ledger.state_inconclusive],
          ['Last observation', fmtAge(ledger.ledger_last_seen) + ' ago'],
        ])}
        {activity && statCard('Verdict activity (24h)', [
          ['Evaluations', Number(activity.evaluations_24h).toLocaleString()],
          ['Would-alert', activity.would_alert_24h],
          ['Confirmed transitions', <span style={{ color: Number(activity.confirmed_transitions_24h) > 0 ? '#f44336' : undefined }}>{activity.confirmed_transitions_24h}</span>],
        ])}
        {ledger && statCard('AI adjudicator', [
          ['Entities adjudicated', ledger.ai_adjudicated],
          ['Last adjudication', ledger.last_ai_verdict_at ? fmtAge(ledger.last_ai_verdict_at) + ' ago' : 'idle'],
        ])}
        {ledger && statCard('Operator verdicts', [
          ['Marked benign', ledger.operator_benign],
          ['Marked threat', ledger.operator_threat],
        ])}
        {refdata && statCard('Reference data', [
          ['Cell towers (OpenCelliD)', Number(refdata.towers).toLocaleString()],
          ['Last refresh', <span style={{ color: refdataStale ? '#ff9800' : undefined }}>
            {refdata.opencellid_last_refresh ? fmtAge(refdata.opencellid_last_refresh) + ' ago' : 'never'}{refdataStale ? ' (stale)' : ''}
          </span>],
        ])}
      </Box>

      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1">Detector health</Typography>
        {health?.status && <Chip size="small" label={health.status} color={health.status === 'healthy' ? 'success' : 'default'} />}
      </Box>
      <Table size="small" sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell>Detector</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Observations</TableCell>
            <TableCell align="right">Last updated</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {detectors.map((d, i) => {
            const dormant = isStale(d.last_updated);
            return (
              <TableRow key={d.detector_name || i} hover sx={dormant ? { opacity: 0.55 } : undefined}>
                <TableCell>{d.detector_name}</TableCell>
                <TableCell>
                  {dormant
                    ? <Tooltip title="No heartbeat in over 24h — this detector is not currently feeding the pipeline">
                        <Chip size="small" label="DORMANT" sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                      </Tooltip>
                    : <Chip size="small" label={d.status} color={d.status === 'ACTIVE' ? 'success' : 'default'} />}
                </TableCell>
                <TableCell align="right">{d.observations_count ?? '—'}</TableCell>
                <TableCell align="right">{fmtAge(d.last_updated)}</TableCell>
              </TableRow>
            );
          })}
          {detectors.length === 0 && (
            <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">No detector heartbeats yet.</Typography></TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Feedback / false-positive calibration</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Type / Rule</TableCell>
            <TableCell align="right">True+</TableCell>
            <TableCell align="right">False+</TableCell>
            <TableCell align="right">Unclear</TableCell>
            <TableCell align="right">FP rate</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fbRows.map((r, i) => (
            <TableRow key={i} hover>
              <TableCell>{r.anomaly_type || r.rule || '—'}</TableCell>
              <TableCell align="right">{r.true_positive ?? 0}</TableCell>
              <TableCell align="right">{r.false_positive ?? 0}</TableCell>
              <TableCell align="right">{r.unclear ?? 0}</TableCell>
              <TableCell align="right">{r.fp_rate != null ? `${Math.round(r.fp_rate)}%` : '—'}</TableCell>
            </TableRow>
          ))}
          {fbRows.length === 0 && (
            <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">No feedback recorded yet.</Typography></TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Proximity panel — operator -> TAK mapping + actionable feed
// ---------------------------------------------------------------------------

const ProximityPanel: React.FC = () => {
  const [rows, setRows] = useState<OperatorTakMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [deviceId, setDeviceId] = useState('');
  const [callsign, setCallsign] = useState('');
  const [uid, setUid] = useState('');
  const [label, setLabel] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await dfpAdminService.listOperatorTak());
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load operator mappings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setError(null);
    try {
      await dfpAdminService.upsertOperatorTak({
        operator_device_id: deviceId.trim(),
        tak_callsign: callsign.trim(),
        tak_uid: uid.trim() || undefined,
        label: label || undefined,
      });
      setDeviceId(''); setCallsign(''); setUid(''); setLabel('');
      setInfo('Mapping saved.');
      load();
    } catch (e: any) {
      setError(e.message || 'Could not save mapping');
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Map each operator's scanner device (the value in <code>signal_*.device_id</code> /
        phone_app_id) to their ATAK callsign. When a watchlisted threat is detected near that
        operator's live position, a directed warning is routed to their EUD. Requires
        <code> DFP_PROXIMITY_ENABLED=true</code> and <code>TAK_ENABLED=true</code> on the engine.
      </Alert>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <TextField size="small" label="Operator device id" value={deviceId}
          onChange={e => setDeviceId(e.target.value)} sx={{ width: 220 }} />
        <TextField size="small" label="ATAK callsign" value={callsign}
          onChange={e => setCallsign(e.target.value)} sx={{ width: 160 }} />
        <TextField size="small" label="ATAK uid (optional)" value={uid}
          onChange={e => setUid(e.target.value)} sx={{ width: 200 }} />
        <TextField size="small" label="Label (optional)" value={label}
          onChange={e => setLabel(e.target.value)} sx={{ width: 160 }} />
        <Button variant="contained" onClick={save} disabled={!deviceId.trim() || !callsign.trim()}>Save mapping</Button>
        <IconButton onClick={load}><RefreshIcon /></IconButton>
      </Box>

      {loading ? <CircularProgress size={24} /> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Operator device id</TableCell>
              <TableCell>Callsign</TableCell>
              <TableCell>EUD uid</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(m => (
              <TableRow key={m.id} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{m.operator_device_id}</TableCell>
                <TableCell>{m.tak_callsign}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{m.tak_uid || '—'}</TableCell>
                <TableCell>{m.label || '—'}</TableCell>
                <TableCell>{m.enabled ? 'yes' : 'no'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={async () => { await dfpAdminService.deleteOperatorTak(m.id); load(); }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">No operator mappings yet.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const TABS = [
  { label: 'Alerts', icon: <ShieldIcon fontSize="small" /> },
  { label: 'Insights', icon: <InsightsIcon fontSize="small" /> },
  { label: 'Watchlist', icon: <WatchlistIcon fontSize="small" /> },
  { label: 'Trusted', icon: <TrustIcon fontSize="small" /> },
  { label: 'Baseline', icon: <BaselineIcon fontSize="small" /> },
  { label: 'Detectors', icon: <HealthIcon fontSize="small" /> },
  { label: 'Proximity', icon: <ProximityIcon fontSize="small" /> },
];

const DFPInsightsPage: React.FC = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', pb: 4 }}>
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 2, borderRadius: { xs: 0, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <ShieldIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">DFP — Digital Force Protection</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Digital Force Protection: actionable threat alerts, the broader anomaly insight feed,
          watchlist and trusted-device management, baseline review, detector health, and operator
          proximity warnings.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: { xs: 0, sm: 2 }, mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {TABS.map((t, i) => (
            <Tab key={t.label} icon={t.icon} iconPosition="start" label={t.label} sx={{ minHeight: 56 }} value={i} />
          ))}
        </Tabs>
      </Paper>

      <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {tab === 0 && <DFPAlertsTab category="actionable" />}
        {tab === 1 && <DFPAlertsTab category="insights" />}
        {tab === 2 && <WatchlistPanel />}
        {tab === 3 && <TrustedPanel />}
        {tab === 4 && <BaselinePanel />}
        {tab === 5 && <HealthPanel />}
        {tab === 6 && <ProximityPanel />}
      </Box>
    </Box>
  );
};

export default DFPInsightsPage;
